import { useMemo } from "react";
import { Box, Chip, Divider, Paper, Stack, Typography } from "@mui/material";

type ActionLog = {
  eventId?: string;
  type?: string;
  path?: string;
  url?: string;
  element?: string;
  elementValue?: string;
  value?: string;
  shadowHost?: string;
  shadowPath?: string;
  timestamp?: number;
  isIframe?: boolean;
  isShadow?: boolean;
  waitFor?: number;
};

interface ActionsTabProps {
  actionLogs: ActionLog[];
  recordingStartTime?: number;
  onSeekToTime?: (timeInSeconds: number) => void;
  bugCreatedAt?: string | number | Date;
}

const ActionsTab = ({
  actionLogs,
  recordingStartTime = 0,
  onSeekToTime,
  bugCreatedAt,
}: ActionsTabProps) => {
  const actions = actionLogs?.length ? actionLogs : [];

  const sortedActions = useMemo(() => {
    return [...actions].sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
  }, [actions]);

  // Calculate the earliest action timestamp as the video start reference
  // Action logs use absolute timestamps, we need to find the earliest one
  const earliestActionTimestamp = useMemo(() => {
    if (!actions.length) return 0;
    let earliest = Infinity;
    actions.forEach((action) => {
      if (action.timestamp && action.timestamp < earliest) {
        earliest = action.timestamp;
      }
    });
    return earliest === Infinity ? 0 : earliest;
  }, [actions]);

  const stats = useMemo(() => {
    const total = actions.length;
    const uniquePages = new Set(
      actions.map((action) => action.path || action.url).filter(Boolean),
    ).size;
    const types = new Set(actions.map((action) => action.type).filter(Boolean))
      .size;
    const avgWait = total
      ? Math.round(
          actions.reduce((sum, action) => sum + (action.waitFor ?? 0), 0) /
            total,
        )
      : 0;
    return { total, uniquePages, types, avgWait };
  }, [actions]);

  const formatTime = (timestamp?: number) => {
    if (!timestamp) return "—";
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(timestamp));
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case "click":
        return "primary";
      case "input":
        return "secondary";
      case "scroll":
        return "info";
      case "navigation":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        height: "100%",
        overflow: "auto",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: "background.default",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Action Logs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Captured UI interactions for this session.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={`${stats.total} actions`}
              color="primary"
              variant="outlined"
            />
            <Chip label={`${stats.types} types`} variant="outlined" />
            <Chip label={`${stats.uniquePages} pages`} variant="outlined" />
            <Chip label={`${stats.avgWait}ms avg wait`} />
          </Stack>
        </Stack>
      </Paper>

      {actions.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            No actions recorded
          </Typography>
          <Typography variant="body2">
            Actions will appear here once a session is captured.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {sortedActions.map((action) => (
            <Paper
              key={action.eventId}
              variant="outlined"
              onClick={() => {
                if (onSeekToTime && action.timestamp) {
                  // Use earliest action timestamp as base for video seeking
                  const relativeTimeSec =
                    (action.timestamp - earliestActionTimestamp) / 1000;

                  if (relativeTimeSec >= 0) {
                    onSeekToTime(relativeTimeSec);
                  }
                }
              }}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "background.paper",
                cursor: onSeekToTime ? "pointer" : "default",
                transition: "all 0.2s ease",
                "&:hover": onSeekToTime
                  ? {
                      bgcolor: "action.hover",
                      borderColor: "primary.main",
                    }
                  : {},
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "180px 1fr",
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Stack spacing={1}>
                    <Chip
                      size="small"
                      color={getTypeColor(action.type)}
                      label={action.type?.toUpperCase() || "EVENT"}
                      sx={{ fontWeight: 600 }}
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Timestamp
                      </Typography>
                      <Typography variant="body2">
                        {formatTime(action.timestamp)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Wait
                      </Typography>
                      <Typography variant="body2">
                        {action.waitFor ?? 0}ms
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {action.isShadow && (
                        <Chip size="small" label="Shadow DOM" />
                      )}
                      {action.isIframe && <Chip size="small" label="iFrame" />}
                    </Stack>
                  </Stack>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    {action.element || "Unknown element"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {action.url || "—"}
                  </Typography>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Path
                      </Typography>
                      <Typography variant="body2">
                        {action.path || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Value
                      </Typography>
                      <Typography variant="body2">
                        {action.value || action.elementValue || "—"}
                      </Typography>
                    </Box>
                    {action.shadowPath && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Shadow Path
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.5,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                          }}
                        >
                          {action.shadowPath}
                        </Typography>
                      </Box>
                    )}
                    {action.shadowHost && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Shadow Host
                        </Typography>
                        <Typography variant="body2">
                          {action.shadowHost}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ActionsTab;
